import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { PROBLEM_CODES, api, errorMessage, invalidField, problemCode, useApiMutation } from "../../api";
import type { Branding } from "../../api";
import { useBranding, useReloadBranding } from "../../branding";
import { Badge, Button, Card, ConfirmDialog, Field, FileInput, useToast } from "../../components";
import { PageHeader } from "../../shell";
import { HEX_COULEUR, LOGO_MAX_BYTES, LOGO_TYPES, accentOverride, contrastRatio } from "../../theme/brand";
import styles from "../parametres/Parametres.module.css";

/**
 * L'identité de la plateforme (marque blanche), réglée par l'administrateur
 * ESN : nom de l'ESN, nom de la plateforme, couleur, signature des courriels,
 * logo. Tout s'applique sans redéploiement — la vitrine, les en-têtes, les
 * courriels et l'aperçu des liens partagés. Le nom de domaine, lui, reste
 * celui du déploiement.
 */
export function EsnMarquePage() {
  const branding = useBranding();
  const recharger = useReloadBranding();

  return (
    <>
      <PageHeader
        eyebrow="Espace ESN"
        title="Marque de la plateforme"
        description="Le nom, la couleur et la signature que voient vos clients, vos consultants et les destinataires de vos courriels. Le nom de domaine ne change pas."
      />
      <div className={styles.formulaire}>
        <FormulaireIdentite branding={branding} onUpdated={recharger} />
        <FormulaireLogoPlateforme branding={branding} onUpdated={recharger} />
      </div>
    </>
  );
}

type Proprietes = { branding: Branding; onUpdated: () => void };

function FormulaireIdentite({ branding, onUpdated }: Proprietes) {
  const { notify } = useToast();
  const maj = useApiMutation(api.updateBranding);
  const [saisie, setSaisie] = useState(branding);
  useEffect(() => setSaisie(branding), [branding]);

  const valide = HEX_COULEUR.test(saisie.accent_color);
  const contraste = valide ? contrastRatio(saisie.accent_color, "#ffffff") : 0;
  const champ = invalidField(maj.error);
  const erreurDe = (nom: string) => (champ === nom ? errorMessage(maj.error) : undefined);
  const modifie =
    saisie.esn_name !== branding.esn_name ||
    saisie.product_short !== branding.product_short ||
    saisie.product_name !== branding.product_name ||
    saisie.accent_color !== branding.accent_color ||
    saisie.mail_signature !== branding.mail_signature;

  function changer(nom: keyof Branding, valeur: string) {
    setSaisie((s) => ({ ...s, [nom]: valeur }));
    maj.reset();
  }

  async function enregistrer(e: FormEvent) {
    e.preventDefault();
    if (!valide) {
      return;
    }
    const b = await maj.run({
      esn_name: saisie.esn_name,
      product_short: saisie.product_short,
      product_name: saisie.product_name,
      accent_color: saisie.accent_color,
      mail_signature: saisie.mail_signature,
    });
    if (b === null) {
      return;
    }
    onUpdated();
    notify({ tone: "success", title: "Marque enregistrée", description: "Elle s'applique dès maintenant, courriels compris." });
  }

  const erreurGenerale =
    maj.error !== null && champ === null
      ? problemCode(maj.error) === PROBLEM_CODES.invalidAccentColor
        ? "Indiquez une couleur au format #rrggbb."
        : errorMessage(maj.error)
      : null;

  return (
    <Card title="Identité" subtitle="Repris par la vitrine, les en-têtes des espaces, les titres de page et les courriels.">
      <form className={styles.formulaire} onSubmit={(e) => void enregistrer(e)} noValidate>
        <Field label="Nom de l'ESN" required error={erreurDe("esn_name")} hint="Qui opère la plateforme : « Réservé aux entreprises clientes de … ».">
          {(commande) => <input {...commande} value={saisie.esn_name} maxLength={120} onChange={(e) => changer("esn_name", e.target.value)} />}
        </Field>
        <Field label="Sigle de la plateforme" required error={erreurDe("product_short")} hint="12 caractères au plus : en-têtes, onglets et sujets des courriels.">
          {(commande) => <input {...commande} value={saisie.product_short} maxLength={12} onChange={(e) => changer("product_short", e.target.value)} />}
        </Field>
        <Field label="Nom de la plateforme" required error={erreurDe("product_name")}>
          {(commande) => <input {...commande} value={saisie.product_name} maxLength={120} onChange={(e) => changer("product_name", e.target.value)} />}
        </Field>
        <Field
          label="Signature des courriels"
          error={erreurDe("mail_signature")}
          hint={`Vide : « L'équipe ${saisie.esn_name.trim() || "…"} ».`}
        >
          {(commande) => (
            <textarea {...commande} rows={2} maxLength={500} value={saisie.mail_signature} onChange={(e) => changer("mail_signature", e.target.value)} />
          )}
        </Field>
        <Field label="Couleur d'accent" error={valide ? undefined : "Format #rrggbb."} hint="Boutons, liens et repère de navigation ; les entreprises peuvent avoir la leur dans leur espace.">
          {(commande) => (
            <div className={styles.couleur}>
              <input
                type="color"
                aria-label="Choisir la couleur"
                value={valide ? saisie.accent_color : branding.accent_color}
                onChange={(e) => changer("accent_color", e.target.value)}
              />
              <input {...commande} type="text" value={saisie.accent_color} onChange={(e) => changer("accent_color", e.target.value.trim().toLowerCase())} />
            </div>
          )}
        </Field>
        <div className={styles.apercu} style={accentOverride(valide ? saisie.accent_color : branding.accent_color)} aria-label="Aperçu de la couleur">
          <Button variant="primary" type="button">
            Bouton principal
          </Button>
          <a className={styles.apercuLien} href="#apercu" onClick={(e) => e.preventDefault()}>
            Un lien
          </a>
          <Badge tone="accent">{saisie.product_short || "…"}</Badge>
          <span className={styles.apercuNav}>Page courante</span>
        </div>
        {valide && (
          <p className={styles.contraste}>
            Contraste du blanc sur cette couleur : {contraste.toFixed(1)}:1.{" "}
            {contraste >= 4.5 ? "Le texte des boutons restera blanc." : "Trop faible pour du texte blanc : le texte des boutons sera sombre."}
          </p>
        )}
        {erreurGenerale !== null && (
          <p className={styles.contraste} role="alert">
            {erreurGenerale}
          </p>
        )}
        <div className={styles.boutons}>
          <Button type="submit" variant="primary" loading={maj.pending} disabled={!valide || !modifie}>
            Enregistrer la marque
          </Button>
        </div>
      </form>
    </Card>
  );
}

function FormulaireLogoPlateforme({ branding, onUpdated }: Proprietes) {
  const { notify } = useToast();
  const [fichier, setFichier] = useState<File | null>(null);
  const [erreurFichier, setErreurFichier] = useState<string | null>(null);
  const [confirmerRetrait, setConfirmerRetrait] = useState(false);
  const envoi = useApiMutation((f: File) => api.uploadBrandingLogo(f));
  const retrait = useApiMutation(() => api.deleteBrandingLogo());

  async function enregistrer() {
    if (fichier === null || (await envoi.run(fichier)) === null) {
      return;
    }
    setFichier(null);
    onUpdated();
    notify({ title: "Logo enregistré", tone: "success" });
  }

  async function retirer() {
    if ((await retrait.run()) === null) {
      notify({ title: "Retrait impossible", description: errorMessage(retrait.error), tone: "danger" });
      return;
    }
    setConfirmerRetrait(false);
    onUpdated();
    notify({ title: "Logo retiré", tone: "neutral" });
  }

  const code = problemCode(envoi.error);
  const erreurEnvoi =
    code === PROBLEM_CODES.logoTooLarge
      ? "Le fichier dépasse 512 Kio."
      : code === PROBLEM_CODES.logoUnsupportedType
        ? "Le contenu du fichier n'est pas une image PNG, JPEG ou WebP."
        : envoi.error !== null
          ? errorMessage(envoi.error)
          : null;

  return (
    <Card title="Logo" subtitle="PNG, JPEG ou WebP, 512 Kio au plus. Il remplace le sigle dans les en-têtes et sert d'icône d'onglet.">
      <div className={styles.formulaire}>
        {branding.logo_url !== null ? (
          <img src={branding.logo_url} alt={`Logo de ${branding.product_short}`} style={{ maxHeight: "4rem", maxWidth: "12rem", objectFit: "contain" }} />
        ) : (
          <p className={styles.contraste}>Aucun logo : le sigle « {branding.product_short} » est affiché.</p>
        )}
        <Field label="Nouveau logo" error={erreurFichier ?? erreurEnvoi ?? undefined}>
          {(commande) => (
            <FileInput
              id={commande.id}
              accept={LOGO_TYPES}
              maxBytes={LOGO_MAX_BYTES}
              value={fichier}
              onChange={(f, err) => {
                setFichier(f);
                setErreurFichier(err);
                envoi.reset();
              }}
              disabled={envoi.pending}
            />
          )}
        </Field>
        <div className={styles.boutons}>
          <Button variant="primary" onClick={() => void enregistrer()} loading={envoi.pending} disabled={fichier === null}>
            Enregistrer le logo
          </Button>
          {branding.logo_url !== null && (
            <Button variant="ghost" onClick={() => setConfirmerRetrait(true)}>
              Retirer le logo
            </Button>
          )}
        </div>
      </div>
      <ConfirmDialog
        open={confirmerRetrait}
        title="Retirer le logo de la plateforme ?"
        description="Le sigle reprendra sa place dans les en-têtes."
        confirmLabel="Retirer"
        tone="danger"
        pending={retrait.pending}
        onConfirm={() => void retirer()}
        onCancel={() => setConfirmerRetrait(false)}
      />
    </Card>
  );
}
